import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { getFacilityAssignmentError } from '../utils/facilityErrors';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Bus, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';
import MobileInput from './ui/mobile-input';
import { validateMobile } from '../utils/phoneValidation';

const Transport = () => {
  const { user, canManageFeature } = useAuth();
  const { showBranchLabels, formatLabel } = useBranchLabels();
  const canManage = canManageFeature('transport');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [routeForm, setRouteForm] = useState({
    route_name: '',
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    fare: '',
  });
  const [driverPhoneError, setDriverPhoneError] = useState('');
  const [allocForm, setAllocForm] = useState({ student_id: '', route_id: '' });
  const [allocFacilityError, setAllocFacilityError] = useState(null);

  const schoolParams = user?.school_id ? { school_id: user.school_id } : {};

  const loadAll = useCallback(async () => {
    try {
      const [routesRes, allocRes, studentsRes] = await Promise.all([
        api.get('/transport/routes', { params: schoolParams }),
        api.get('/transport/allocations', { params: schoolParams }),
        api.get('/students', { params: schoolParams }),
      ]);
      setRoutes(routesRes.data.data || []);
      setAllocations(allocRes.data.data || []);
      setStudents(studentsRes.data.data || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load transport data', variant: 'destructive' });
    }
  }, [user?.school_id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    if (!routeForm.route_name.trim()) return;
    const err = validateMobile(routeForm.driver_phone);
    if (err) {
      setDriverPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setDriverPhoneError('');
    try {
      await api.post('/transport/routes', {
        ...routeForm,
        driver_phone: routeForm.driver_phone || null,
        school_id: user?.school_id,
        fare: routeForm.fare ? parseFloat(routeForm.fare) : null,
      });
      toast({ title: 'Route created' });
      setRouteForm({ route_name: '', vehicle_number: '', driver_name: '', driver_phone: '', fare: '' });
      loadAll();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to create route',
        variant: 'destructive',
      });
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    if (!allocForm.student_id || !allocForm.route_id) return;
    try {
      setAllocFacilityError(null);
      await api.post('/transport/allocations', {
        student_id: parseInt(allocForm.student_id, 10),
        route_id: parseInt(allocForm.route_id, 10),
      });
      toast({ title: 'Student allocated to route' });
      setAllocForm({ student_id: '', route_id: '' });
      loadAll();
    } catch (err) {
      const { message, profilePath } = getFacilityAssignmentError(err);
      if (profilePath) {
        setAllocFacilityError({ message, profilePath });
      }
      toast({
        title: 'Cannot allocate',
        description: profilePath
          ? `${message} Enable transport on the student profile first.`
          : message,
        variant: 'destructive',
      });
    }
  };

  const handleRemoveAlloc = async (studentId) => {
    try {
      await api.delete(`/transport/allocations/${studentId}`);
      toast({ title: 'Allocation removed' });
      loadAll();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to remove',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Bus className="w-7 h-7" /> Transport
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Routes, vehicles, drivers, and student allocations</p>
      </div>

      <Tabs defaultValue="routes">
        <TabsList>
          <TabsTrigger value="routes">Routes</TabsTrigger>
          <TabsTrigger value="allocations">Student allocation</TabsTrigger>
        </TabsList>

        <TabsContent value="routes" className="space-y-4">
          {canManage && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg">Add route</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateRoute} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <Label>Route name</Label>
                    <Input
                      value={routeForm.route_name}
                      onChange={(e) => setRouteForm({ ...routeForm, route_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Vehicle number</Label>
                    <Input
                      value={routeForm.vehicle_number}
                      onChange={(e) => setRouteForm({ ...routeForm, vehicle_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Driver name</Label>
                    <Input
                      value={routeForm.driver_name}
                      onChange={(e) => setRouteForm({ ...routeForm, driver_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Driver phone</Label>
                    <MobileInput
                      value={routeForm.driver_phone}
                      onChange={(v) => {
                        setRouteForm({ ...routeForm, driver_phone: v });
                        setDriverPhoneError('');
                      }}
                      error={driverPhoneError}
                    />
                  </div>
                  <div>
                    <Label>Fare (optional)</Label>
                    <Input
                      type="number"
                      value={routeForm.fare}
                      onChange={(e) => setRouteForm({ ...routeForm, fare: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Plus className="w-4 h-4 mr-1" /> Add route
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Route</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Fare</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No routes yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    routes.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium flex items-center gap-2 flex-wrap">
                          {r.route_name}
                          <BranchLabel name={r.branch_name} show={showBranchLabels} />
                        </TableCell>
                        <TableCell>{r.vehicle_number || '—'}</TableCell>
                        <TableCell>{r.driver_name || '—'}</TableCell>
                        <TableCell>{r.driver_phone || '—'}</TableCell>
                        <TableCell>{r.fare != null ? `₹${r.fare}` : '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocations" className="space-y-4">
          {canManage && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg">Allocate student</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAllocate} className="flex flex-wrap gap-3 items-end">
                  <div className="min-w-[200px] flex-1">
                    <Label>Student</Label>
                    <Select
                      value={allocForm.student_id}
                      onValueChange={(v) => setAllocForm({ ...allocForm, student_id: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>
                        {students.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {s.first_name} {s.last_name} ({s.admission_no})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <Label>Route</Label>
                    <Select
                      value={allocForm.route_id}
                      onValueChange={(v) => setAllocForm({ ...allocForm, route_id: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                      <SelectContent>
                        {routes.map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>{formatLabel(r.route_name, r)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Allocate</Button>
                </form>
                {allocFacilityError && (
                  <p className="text-sm text-red-400 mt-3">
                    {allocFacilityError.message}{' '}
                    <button
                      type="button"
                      className="underline font-medium text-red-300 hover:text-red-200"
                      onClick={() => navigate(allocFacilityError.profilePath)}
                    >
                      Open student profile
                    </button>
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Admission</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Vehicle</TableHead>
                    {canManage && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No allocations
                      </TableCell>
                    </TableRow>
                  ) : (
                    allocations.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="flex items-center gap-2 flex-wrap">
                          {a.first_name} {a.last_name}
                          <BranchLabel name={a.branch_name} show={showBranchLabels} />
                        </TableCell>
                        <TableCell>{a.admission_no}</TableCell>
                        <TableCell>{a.route_name}</TableCell>
                        <TableCell>{a.vehicle_number || '—'}</TableCell>
                        {canManage && (
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveAlloc(a.student_id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Transport;
